import PropTypes from 'prop-types';
import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { ButtonMenu } from '@cld/button';
import { Icon } from '@cld/icon';
import { TypographyProps } from '@cld/typography';
import CollapseContext from './collapsible/CollapseContext';
import AssetUrlContainer from './container/AssetUrlContainer';
import ScrollableLinesWrapContainer from './container/ScrollableLinesWrapContainer';
import CollapsibleContent from './collapsible/CollapsibleContent';
import CopyableAssetUrl from './CopyableAssetUrl';
import SectionsFromObject from './sections/SectionsFromObject';
import { Ellipsis, Kind, Prefix, PublicId, Signature, Transformation, Version } from './sections/Sections';
import { theme as t } from './AssetUrlTheme';

export interface AssetUrlProps {
  /** URL sections */
  children: ReactNode;
  /** If collapsed initially */
  isCollapsedInitially?: boolean;
  /** If should show the collapse button */
  showCollapseButton?: boolean;
  /** Expand button icon when collapsed */
  collapsedIcon?: ReactNode;
  /** Expand button icon when expanded */
  expandedIcon?: ReactNode;
  /** Size of the text */
  size?: TypographyProps['size'];
}

const defaultCollapsedIcon = <Icon name="cloud" />;
const defaultExpandedIcon = <Icon name="cloud" />;

const ButtonWithMargin = styled(ButtonMenu)`
  margin-right: ${t.buttonMargin};
  ${Icon} path {
    fill: ${t.iconColor};
  }
`;

export interface AssetUrlSubcomponents {
  Collapsible: typeof CollapsibleContent;
  Copyable: typeof CopyableAssetUrl;
  FromObject: typeof SectionsFromObject;
  // Sections
  Prefix: typeof Prefix;
  Kind: typeof Kind;
  Signature: typeof Signature;
  Transformation: typeof Transformation;
  Version: typeof Version;
  PublicId: typeof PublicId;
  Ellipsis: typeof Ellipsis;
}

/** Main component, provides styling and collapse/expand button */
export const AssetUrl: FC<AssetUrlProps> & AssetUrlSubcomponents = ({
  children,
  isCollapsedInitially = true,
  showCollapseButton = true,
  collapsedIcon = defaultCollapsedIcon,
  expandedIcon = defaultExpandedIcon,
  size = 'sm',
}) => {
  const [collapsed, setCollapsed] = useState(isCollapsedInitially);
  const toggleCollapsed = () => {
    setCollapsed((collapsed) => !collapsed);
  };

  return (
    <AssetUrlContainer>
      <CollapseContext.Provider value={collapsed}>
        {showCollapseButton && (
          <ButtonWithMargin size="sm" shape="pill" onClick={toggleCollapsed}>
            {collapsed ? collapsedIcon : expandedIcon}
          </ButtonWithMargin>
        )}
        <ScrollableLinesWrapContainer size={size}>{children}</ScrollableLinesWrapContainer>
      </CollapseContext.Provider>
    </AssetUrlContainer>
  );
};

AssetUrl.propTypes = {
  children: PropTypes.node.isRequired,
  isCollapsedInitially: PropTypes.bool,
  showCollapseButton: PropTypes.bool,
  collapsedIcon: PropTypes.node,
  expandedIcon: PropTypes.node,
  size: PropTypes.oneOf(['xxl', 'xl', 'lg', 'md', 'sm', 'xs', 'xxs']),
};

AssetUrl.Collapsible = CollapsibleContent;
AssetUrl.Copyable = CopyableAssetUrl;
AssetUrl.FromObject = SectionsFromObject;
// Sections
AssetUrl.Prefix = Prefix;
AssetUrl.Kind = Kind;
AssetUrl.Signature = Signature;
AssetUrl.Transformation = Transformation;
AssetUrl.Version = Version;
AssetUrl.PublicId = PublicId;
AssetUrl.Ellipsis = Ellipsis;
